import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ConnectionStatus,
  getCurrentConnectionStatus,
  startPolling,
  stopPolling,
  subscribe,
} from 'utils/connectionStatus';

interface ConnectionStatusContextValue {
  isOnline: boolean;
  status: ConnectionStatus;
}

const ConnectionStatusContext = createContext<ConnectionStatusContextValue>({
  isOnline: true,
  status: 'online',
});

/**
 * Hook to access connection status.
 *
 * @returns {{ isOnline: boolean, status: 'online' | 'offline' }}
 *
 * @example
 * const { isOnline, status } = useConnectionStatus();
 * if (!isOnline) {
 *   return <OfflineMessage />;
 * }
 */
export const useConnectionStatus = (): ConnectionStatusContextValue =>
  useContext(ConnectionStatusContext);

interface ConnectionStatusProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for connection status.
 * Wrap your app with this to enable useConnectionStatus hook.
 *
 * @example
 * <ConnectionStatusProvider>
 *   <App />
 * </ConnectionStatusProvider>
 */
export const ConnectionStatusProvider = ({
  children,
}: ConnectionStatusProviderProps): React.ReactElement => {
  const [status, setStatus] = useState<ConnectionStatus>(
    getCurrentConnectionStatus,
  );

  useEffect(() => {
    // Start listening to connection changes
    startPolling();

    // Subscribe to status changes
    const unsubscribe = subscribe((newStatus: ConnectionStatus) => {
      setStatus(newStatus);
    });

    return () => {
      unsubscribe();
      stopPolling();
    };
  }, []);

  const value = useMemo<ConnectionStatusContextValue>(
    () => ({
      isOnline: status === 'online',
      status,
    }),
    [status],
  );

  return (
    <ConnectionStatusContext.Provider value={value}>
      {children}
    </ConnectionStatusContext.Provider>
  );
};
